import { useEffect, useState } from 'react';

import { Icon } from '@ui-component-library/components/Icon';
import { Tooltip } from '@ui-component-library/components/Tooltip';

import type { Option, SelectorProps } from './Selector.types';

import { S } from './Selector.styles';

export const Selector = ({
  label,
  description,
  options,
  onChange,
  name,
  disabled = false,
  variant = 'default',
  values,
  tabIndex = 0,
  multiSelect = false
}: SelectorProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (values && values.length > 0) {
      const selected = options.filter((option) => values.includes(option.value));
      setSelectedOptions(selected);
      onChange(selected, name);
      return;
    }

    // If options are only one  item it will be selected by default
    if (options.length === 1) {
      setSelectedOptions([options[0]]);
      onChange([options[0]], name);
      return;
    }

    // Check if selected/preset options has changed
    const validSelectedOptions = selectedOptions.filter((selected) =>
      options.some((opt) => opt.value === selected.value)
    );

    if (validSelectedOptions.length !== selectedOptions.length) {
      setSelectedOptions(validSelectedOptions);
      onChange(validSelectedOptions, name);
    }
  }, [options, values, name]);

  useEffect(() => {
    onChange(selectedOptions, name);
  }, [selectedOptions, onChange, name]);

  const handleOptionChange = (option: Option, isDisabled: boolean) => {
    if (isDisabled) {
      return;
    }
    const isAlreadySelected = selectedOptions.some((opt) => opt.value === option.value);

    if (isAlreadySelected) {
      setSelectedOptions(selectedOptions.filter((opt) => opt.value !== option.value));
    } else {
      setSelectedOptions(multiSelect ? [...selectedOptions, option] : [option]);
    }
  };

  return (
    <S.Wrapper $disabled={disabled}>
      <S.TextContainer>
        <S.Label>
          {label}
          {variant === 'fluid' && (
            <Tooltip content={description} aria-label={description}>
              <Icon name="InfoFilled" size="medium" color="pebble-500" />
            </Tooltip>
          )}
        </S.Label>
        {description && variant === 'default' && <S.Description>{description}</S.Description>}
      </S.TextContainer>
      <S.Options>
        {options.map((option) => {
          const isSelected = selectedOptions.some((opt) => opt.value === option.value);
          const isOptionDisabled = disabled || options.length === 1;
          const optionId = option?.id ?? `option-${option.value}`;

          return (
            <S.Option key={option.value} $selected={isSelected} $disabled={isOptionDisabled}>
              <S.HiddenCheckbox
                $checked={isSelected}
                id={optionId}
                type="checkbox"
                checked={isSelected}
                onChange={() =>
                  handleOptionChange({ ...option, id: optionId }, options.length === 1)
                }
                disabled={isOptionDisabled}
                tabIndex={tabIndex}
              />

              <S.OptionContent as="label" htmlFor={optionId} $disabled={isOptionDisabled}>
                {option?.image && <S.Image src={option.image} alt={option.label} />}
                <S.OptionTextsContainer>
                  <S.OptionLabel>{option.label}</S.OptionLabel>
                  {option.description && (
                    <S.OptionDescription>{option.description}</S.OptionDescription>
                  )}
                </S.OptionTextsContainer>
                <S.CustomCheckbox $checked={isSelected}>
                  {isSelected && <Icon name="CheckSimple" size="small" color="white-50" />}
                </S.CustomCheckbox>
              </S.OptionContent>
            </S.Option>
          );
        })}
      </S.Options>
    </S.Wrapper>
  );
};
